import type { LetterHead, Team, TeamMemberWContact } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { Input } from "@repo/ui/components/Base/Input";
import { Formik } from "formik";
import orgLogoPlacholder from "../../assets/orgLogoPlaceholder.png";
import { useEffect, useState } from "react";
import { FileUpload } from "@repo/ui/components/Base/FileUpload";
import { Heading } from "@repo/ui/components/Base/Heading";
import {
  ArrowDownCircleIcon,
  ArrowUpCircleIcon,
  MinusCircleIcon,
  PlusCircleIcon,
} from "lucide-react";
import { storage } from "@/services/storage";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/Base/Dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/components/Base/Select";
import * as yup from "yup";
import { useBlockUnload } from "@repo/ui/components/Base/hooks/useBlockUnload";
import { arrayify, cn } from "@repo/shared/utils";
import { extractExtension } from "@repo/lib/extractExtension";
import { useTeamList } from "@/hooks/queryHooks/teamHooks";
import { letterHeadRepo } from "@/repositories/letterHeadRepo";
import { teamMemberRepo } from "@/repositories/teamMemberRepo";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { LetterheadPreviewDialog } from "./LetterheadPreviewDialog";
import { useTenantStore } from "@/stores/tenantStore";
import {
  SidebarLayoutIcon,
  TopAcrossBoardLayoutIcon,
  TopAcrossLayoutIcon,
  TopCenterLayoutIcon,
} from "./LetterheadLayoutIcons";
import { Label } from "@repo/ui/components/Base/Label";
import {
  coerceTheme,
  layoutTable,
  numberFormatList,
  themeList,
  type LayoutType,
} from "@repo/ui/components/Base/agendaLayoutsThemes";
import { CheckboxInput } from "@repo/ui/components/Base/CheckboxInput";
import { Tooltip } from "@repo/ui/components/Base/Tooltip";
import { useActiveTeam } from "@/hooks/useActiveTeam";

type OfficerInfoFormProps = {
  title: string;
  officerDetails: TeamMemberWContact;
  onChange: (officerDetails: TeamMemberWContact) => void;
  onRemove: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  disabled?: boolean;
};
const OfficerInfoForm = ({
  title,
  officerDetails,
  onChange,
  onRemove,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  disabled = false,
}: OfficerInfoFormProps) => {
  return (
    <div className="mx-auto mt-8 border bg-white p-4">
      <div className="flex flex-row justify-between">
        <div className="flex flex-row gap-8">
          <Heading size="h3" title={title} />
          <div>
            <Button
              disabled={disabled || !canMoveUp}
              variant="ghost"
              type="button"
              size="icon"
              onClick={() => onMoveUp && onMoveUp()}
            >
              <ArrowUpCircleIcon />
            </Button>
            <Button
              disabled={disabled || !canMoveDown}
              variant="ghost"
              type="button"
              size="icon"
              onClick={() => onMoveDown && onMoveDown()}
            >
              <ArrowDownCircleIcon />
            </Button>
          </div>
        </div>
        <Button
          disabled={disabled}
          variant="ghost"
          tooltip="Remove Officer from Letterhead"
          type="button"
          size="icon"
          onClick={() => onRemove()}
        >
          <MinusCircleIcon />
        </Button>
      </div>
      <div>
        <div className="grid grid-cols-1 md:grid-cols-3">
          <Input
            id="firstName"
            label="First Name"
            name="firstName"
            disabled={disabled}
            orientation="horizontal"
            value={officerDetails.contactInfo.firstName ?? ""}
            onChange={(event) =>
              onChange({
                ...officerDetails,
                contactInfo: {
                  ...officerDetails.contactInfo,
                  firstName: event.target.value,
                },
              })
            }
            labelStyle="w-[128px] text-right"
          />
          <Input
            id="lastName"
            label="Last Name"
            name="lastName"
            disabled={disabled}
            orientation="horizontal"
            value={officerDetails.contactInfo.lastName ?? ""}
            onChange={(event) =>
              onChange({
                ...officerDetails,
                contactInfo: {
                  ...officerDetails.contactInfo,
                  lastName: event.target.value,
                },
              })
            }
            labelStyle="w-[128px] text-right"
          />
          <Input
            id="title"
            label="Position"
            name="title"
            disabled={disabled}
            orientation="horizontal"
            value={officerDetails.title ?? ""}
            onChange={(event) =>
              onChange({
                ...officerDetails,
                title: event.target.value,
              })
            }
            labelStyle="w-[128px] text-right"
          />
          <Input
            id="email"
            label="Email Address"
            name="email"
            disabled={disabled}
            orientation="horizontal"
            value={officerDetails.contactInfo.email ?? ""}
            onChange={(event) =>
              onChange({
                ...officerDetails,
                contactInfo: {
                  ...officerDetails.contactInfo,
                  email: event.target.value,
                },
              })
            }
            labelStyle="w-[128px] text-right"
          />
          <Input
            id="phoneNumber"
            label="Phone Number"
            name="phoneNumber"
            disabled={disabled}
            orientation="horizontal"
            value={
              arrayify(officerDetails.contactInfo.primaryPhone)[0]?.number ?? ""
            }
            onChange={(event) =>
              onChange({
                ...officerDetails,
                contactInfo: {
                  ...officerDetails.contactInfo,
                  primaryPhone: {
                    type: "work",
                    number: event.target.value,
                  },
                },
              })
            }
            labelStyle="w-[128px] text-right"
          />
        </div>
      </div>
    </div>
  );
};

const SelectTeamDialog = ({
  open,
  setOpen,
  tenantId,
  onAdd,
}: {
  open: boolean;
  setOpen: (arg: boolean) => void;
  tenantId: string;
  onAdd: (arg: Team | undefined) => void;
}) => {
  const [team, setTeam] = useState<Team>();
  const { data: teamsData } = useTeamList(tenantId);

  const handleAdd = () => {
    setOpen(false);
    onAdd(team);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Select Team</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DialogDescription>
            Select a team to get officers to add to the letterhead
          </DialogDescription>
          <div className="pt-4 pb-4">
            <Select
              onValueChange={(value) =>
                setTeam(teamsData?.find((t) => t.id === value))
              }
            >
              <SelectTrigger className="">
                <SelectValue placeholder="Select a Team" />
              </SelectTrigger>
              <SelectContent>
                {teamsData?.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogBody>
        <DialogFooter className="flex flex-row gap-4">
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => handleAdd()}>Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface LayoutButtonProps {
  children?: React.ReactNode;
  tooltip?: React.ReactNode;
  defaultOption?: boolean;
  value: LayoutType;
  setFieldValue?: (field: string, value: string) => void;
  currentValue?: string;
}

const LayoutButton = ({
  children,
  tooltip,
  defaultOption = false,
  value,
  setFieldValue,
  currentValue,
}: LayoutButtonProps) => {
  const selected =
    currentValue === value ||
    (defaultOption && (!currentValue || !(currentValue in layoutTable)));

  return (
    <Button
      onClick={() => setFieldValue?.("layout", value)}
      tooltip={tooltip}
      tooltipProps={{ delayDuration: 300 }}
      className={cn(
        "border border-neutral-300 w-20 h-20 p-0 flex justify-center items-center bg-white hover:bg-neutral-200 text-neutral-400",
        selected && "bg-primary hover:bg-primary text-white"
      )}
    >
      {children}
    </Button>
  );
};

interface LetterHeadFormProps {
  tenantId: string;
  letterHeadFormData?: LetterHead;
  previewOpen?: boolean;
  setPreviewOpen?: (open: boolean) => void;
  onCancel?: () => void;
  onSave?: () => void;
}
export const LetterheadForm = ({
  tenantId,
  letterHeadFormData,
  previewOpen,
  setPreviewOpen,
  onCancel,
  onSave,
}: LetterHeadFormProps) => {
  useBlockUnload();

  const tenant = useTenantStore((s) => s.tenant);
  const team = useActiveTeam() || undefined;

  const [logo, setLogo] = useState<File | undefined>();
  const [open, setOpen] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    if (!previewOpen) {
      setPreviewKey((key) => key + 1);
    }
  }, [previewOpen]);

  const handleLogoUpload = (
    file: File,
    setFieldValue: (field: string, value: string) => void
  ) => {
    setLogo(file);
    const reader = new FileReader();
    reader.onload = () => {
      setFieldValue("logoUrl", reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadOnSave = async (letterHeadId: string) => {
    let logoUri = letterHeadFormData?.logoUrl ?? "";
    if (logo) {
      const extension = extractExtension(logo.name);
      logoUri = await storage.upload(
        logo,
        `${tenantId}/letterheads/${letterHeadId}${extension ? "." + extension : ""}`
      );
    }
    return logoUri;
  };

  const validationSchema = yup.object({
    viewName: yup.string().required("Letterhead title is required"),
  });

  return (
    <div className="max-w-screen-lg w-full mx-auto">
      <Formik
        initialValues={letterHeadFormData ?? letterHeadRepo.init()}
        validationSchema={validationSchema}
        onSubmit={async (values, { resetForm }) => {
          if (!tenantId) {
            console.log("No tenant ID", tenantId);
            return;
          }
          const logoUrl = await uploadOnSave(values.id);

          values.sectionNumbering ??= tenant?.numberFormat ?? "none";

          if (letterHeadFormData) {
            await letterHeadRepo.update(tenantId, values.id, {
              ...values,
              logoUrl,
            });
          } else {
            await letterHeadRepo.create(tenantId, { ...values, logoUrl });
          }

          if (onSave) onSave();
          resetForm();
        }}
      >
        {({
          values,
          handleSubmit,
          handleBlur,
          handleChange,
          setFieldValue,
          resetForm,
          errors,
          isValid,
          touched,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            {previewOpen && (
              <LetterheadPreviewDialog
                key={previewKey}
                open={previewOpen}
                setOpen={setPreviewOpen}
                tenant={tenant}
                team={team}
                letterHead={values}
              />
            )}

            <SelectTeamDialog
              open={open}
              setOpen={setOpen}
              tenantId={tenantId}
              onAdd={(arg) =>
                setFieldValue("members", [
                  ...values.members,
                  ...(arg?.teamMembers ?? []),
                ])
              }
            />
            <h2 className="text-xl font-bold mt-8 ml-4">Details</h2>
            <div className="flex flex-row justify-between gap-x-6">
              <div className="flex flex-col w-1/2">
                <Input
                  id="viewName"
                  label="Letterhead Title"
                  name="viewName"
                  disabled={isSubmitting}
                  orientation="horizontal"
                  value={values.viewName ?? ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.viewName && touched.viewName}
                  errorMessage={errors.viewName}
                  labelStyle="w-[256px] text-right"
                />
                <Input
                  id="name"
                  label="Organization Name"
                  name="name"
                  disabled={isSubmitting}
                  orientation="horizontal"
                  value={values.name ?? ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.name && touched.name}
                  errorMessage={errors.name}
                  labelStyle="w-[256px] text-right"
                />

                <Input
                  id="email"
                  label="Organization Email (Optional)"
                  name="email"
                  disabled={isSubmitting}
                  orientation="horizontal"
                  value={values.email ?? ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.email && touched.email}
                  errorMessage={errors.email}
                  labelStyle="w-[256px] text-right"
                />
                <Input
                  id="website"
                  label="Organization Website (Optional)"
                  name="website"
                  disabled={isSubmitting}
                  orientation="horizontal"
                  value={values.website ?? ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.website && touched.website}
                  errorMessage={errors.website}
                  labelStyle="w-[256px] text-right"
                />

                <h2 className="text-xl font-bold mt-8 ml-4">Styles</h2>

                <div className="flex flex-row items-center gap-3 mb-5">
                  <Label label="Layout" className="w-[256px] text-right" />

                  <div className="w-full flex flex-row gap-1">
                    <LayoutButton
                      defaultOption
                      tooltip={<>Left-hand Sidebar</>}
                      value="sidebar"
                      currentValue={values.layout}
                      setFieldValue={setFieldValue}
                    >
                      <SidebarLayoutIcon />
                    </LayoutButton>
                    <LayoutButton
                      tooltip={<>Top only, in three columns</>}
                      value="topAcross"
                      currentValue={values.layout}
                      setFieldValue={setFieldValue}
                    >
                      <TopAcrossLayoutIcon />
                    </LayoutButton>
                    <LayoutButton
                      tooltip={
                        <>
                          Top only, centered, with
                          <br />
                          board members in two columns
                        </>
                      }
                      value="topCenter"
                      currentValue={values.layout}
                      setFieldValue={setFieldValue}
                    >
                      <TopCenterLayoutIcon />
                    </LayoutButton>
                    <LayoutButton
                      tooltip={
                        <>
                          Top only, in three columns,
                          <br />
                          with board members in two columns
                        </>
                      }
                      value="topAcrossBoard"
                      currentValue={values.layout}
                      setFieldValue={setFieldValue}
                    >
                      <TopAcrossBoardLayoutIcon />
                    </LayoutButton>
                  </div>
                </div>

                <SelectInput
                  label="Theme"
                  labelStyle="w-[256px] text-right"
                  orientation="horizontal"
                  value={coerceTheme(values.theme).id}
                  onValueChange={(value) => {
                    setFieldValue("theme", value);
                  }}
                  options={themeList.map((theme) => (
                    <SelectItem key={theme.id} value={theme.id}>
                      {theme.name}
                    </SelectItem>
                  ))}
                />

                <Tooltip
                  asChild
                  className={cn(values.sectionNumbering != null && "hidden")}
                  label={
                    values.sectionNumbering == null ? (
                      <>
                        This field is inheriting from the deprecated
                        <br />
                        organization setting, 'Numeric list format'.
                        <br />
                        Save the letterhead to apply this choice.
                      </>
                    ) : null
                  }
                >
                  <div className="mt-8 flex flex-row justify-end">
                    <SelectInput
                      containerStyle="w-[80%]"
                      triggerStyle={cn(
                        "mt-1",
                        values.sectionNumbering == null &&
                          "border-2 border-secondary"
                      )}
                      label="Agenda section numbering format:"
                      value={
                        values.sectionNumbering ??
                        tenant?.numberFormat ??
                        "none"
                      }
                      onValueChange={(value) =>
                        setFieldValue("sectionNumbering", value)
                      }
                      options={numberFormatList.map((format) => (
                        <SelectItem key={format.value} value={format.value}>
                          {format.label}
                        </SelectItem>
                      ))}
                    />
                  </div>
                </Tooltip>

                <div className="mt-3 flex flex-row justify-end">
                  <SelectInput
                    containerStyle="w-[80%]"
                    triggerStyle="mt-1"
                    label="Agenda item numbering format:"
                    value={values.itemNumbering ?? "none"}
                    onValueChange={(value) =>
                      setFieldValue("itemNumbering", value)
                    }
                    options={numberFormatList.map((format) => (
                      <SelectItem key={format.value} value={format.value}>
                        {format.label}
                      </SelectItem>
                    ))}
                  />
                </div>

                <CheckboxInput
                  wrapperStyle="mt-3"
                  containerStyle="justify-end"
                  label={
                    <p className="mt-4 leading-6">
                      Prefix item numbers with section number.
                      <br />
                      <span className="italic text-sm">
                        (e.g. section 1, item 1.1, 1.2, 1.3)
                      </span>
                    </p>
                  }
                  orientation="horizontal"
                  checked={values.nestNumbering ?? false}
                  onCheckedChange={(checked) =>
                    setFieldValue("nestNumbering", !!checked)
                  }
                />
              </div>
              <div className="flex flex-col w-1/2">
                <div className="max-w-[250px] mx-auto">
                  Organization Logo:
                  <img
                    src={
                      values.logoUrl === "" ? orgLogoPlacholder : values.logoUrl
                    }
                    width={250}
                    className="mt-2"
                  />
                </div>
                <div className="max-w-[200px] mx-auto mt-5">
                  <FileUpload
                    disabled={isSubmitting}
                    accept="images"
                    handleFileUpload={(file: File) => {
                      if (file !== logo) {
                        handleLogoUpload(file, setFieldValue);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-xl font-bold mt-8 ml-4">Officers</h2>
              <div className="flex flex-col">
                {values.members.map((teamMember, index) => (
                  <OfficerInfoForm
                    key={index}
                    disabled={isSubmitting}
                    canMoveDown={index < values.members.length - 1}
                    canMoveUp={index > 0}
                    title={`Officer #${index + 1} Information`}
                    officerDetails={teamMember}
                    onChange={(officerDetails) => {
                      const members = values.members;
                      members[index] = officerDetails;
                      setFieldValue("members", members);
                    }}
                    onRemove={() => {
                      const members = values.members;
                      members.splice(index, 1);
                      setFieldValue("members", members);
                    }}
                    onMoveUp={() => {
                      const members = values.members;
                      const temp = members[index - 1];
                      members[index - 1] = members[index];
                      members[index] = temp;
                      setFieldValue("members", members);
                    }}
                    onMoveDown={() => {
                      const members = values.members;
                      const temp = members[index + 1];
                      members[index + 1] = members[index];
                      members[index] = temp;
                      setFieldValue("members", members);
                    }}
                  />
                ))}
              </div>
              <div className="flex flex-row justify-around pt-16">
                <Button
                  disabled={isSubmitting}
                  onClick={() => setOpen(!open)}
                  className="mt-4 max-w-[24em] self-end"
                >
                  Add Officers from Team <PlusCircleIcon className="ml-4" />
                </Button>
                <Button
                  disabled={isSubmitting}
                  onClick={() =>
                    setFieldValue("members", [
                      ...values.members,
                      teamMemberRepo.initWithContactInfo(),
                    ])
                  }
                  className="mt-4 max-w-[24em] self-end"
                >
                  Add Officer Manually <PlusCircleIcon className="ml-4" />
                </Button>
              </div>
            </div>
            <div className="flex flex-row justify-between mt-10">
              <Button
                variant="destructive"
                disabled={isSubmitting}
                onClick={() => {
                  if (onCancel) onCancel();
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                type="submit"
                disabled={!isValid || isSubmitting}
              >
                Save
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};
